import { useTranslation } from "react-i18next";

import { Box, InputAdornment, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../formik/FormikDecimalTextField";
import { FormikYesNoAutocomplete } from "../../formik/FormikYesNoAutocomplete";
import { SpousalMaintenanceFieldVisibility, SpousalMaintenanceFormValues } from "../SpousalMaintenance.types";

interface SpousalMaintenanceInputsProps {
  formik: FormikProps<SpousalMaintenanceFormValues>;
  visibility: SpousalMaintenanceFieldVisibility;
  // The "nicht verheiratet" dead end is shown inline under the first question by default. Layouts with
  // a dedicated result area (the internal calculator dialog) render it there instead.
  showNotMarriedMessage?: boolean;
}

export function SpousalMaintenanceInputs({
  formik,
  visibility,
  showNotMarriedMessage = true,
}: SpousalMaintenanceInputsProps) {
  const { t } = useTranslation();

  // Announced via the hidden live region below, so screen-reader users notice when answering a
  // question reveals (or hides) another one further down.
  const visibleQuestionCount = [
    true, // the isMarried question is always visible
    visibility.showSpouseIncome,
    visibility.showHasOwnIncome,
    visibility.showOwnIncome,
    visibility.showLivingTogether,
    visibility.showHousingCosts,
    visibility.showChildren,
  ].filter(Boolean).length;

  return (
    <Stack spacing={1}>
      <Typography variant="body2">{t("spousalMaintenance.intro")}</Typography>

      <FormikYesNoAutocomplete
        formik={formik}
        fieldName="isMarried"
        id="is-married"
        label={t("spousalMaintenance.questions.isMarried")}
        labelAbove
        required
      />

      {visibility.notMarried && showNotMarriedMessage && (
        <Typography color="error.main" sx={{ fontWeight: 600 }}>
          {t("spousalMaintenance.notMarried")}
        </Typography>
      )}

      {visibility.showSpouseIncome && (
        <FormikDecimalTextField
          formik={formik}
          fieldName="spouseIncome"
          id="spouse-income"
          label={t("spousalMaintenance.questions.spouseIncome")}
          labelAbove
          required
          minValue={0}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
        />
      )}

      {visibility.showHasOwnIncome && (
        <FormikYesNoAutocomplete
          formik={formik}
          fieldName="hasOwnIncome"
          id="has-own-income"
          label={t("spousalMaintenance.questions.hasOwnIncome")}
          labelAbove
          required
        />
      )}

      {visibility.showOwnIncome && (
        <FormikDecimalTextField
          formik={formik}
          fieldName="ownIncome"
          id="own-income"
          label={t("spousalMaintenance.questions.ownIncome")}
          labelAbove
          required
          minValue={0}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
        />
      )}

      {visibility.showLivingTogether && (
        <>
          <FormikYesNoAutocomplete
            formik={formik}
            fieldName="livingTogether"
            id="living-together"
            label={t("spousalMaintenance.questions.livingTogether")}
            labelAbove
            required
          />
          {formik.values.livingTogether !== null && (
            <Typography variant="body2" color="text.secondary">
              {formik.values.livingTogether
                ? t("spousalMaintenance.info.livingTogether")
                : t("spousalMaintenance.info.livingApart")}
            </Typography>
          )}
        </>
      )}

      {visibility.showHousingCosts && (
        <FormikDecimalTextField
          formik={formik}
          fieldName="housingCosts"
          id="housing-costs"
          label={t("spousalMaintenance.questions.housingCosts")}
          labelAbove
          required
          minValue={0}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
        />
      )}

      {visibility.showChildren && (
        <FormikDecimalTextField
          formik={formik}
          fieldName="children"
          id="children"
          label={t("spousalMaintenance.questions.children")}
          labelAbove
          required
          minValue={0}
          decimalScale={0}
        />
      )}

      {/* Announces whichever status is current: the dead-end message or the visible-question count.
          Layouts that render the dead-end message elsewhere announce it from there, so it must not be
          repeated here. */}
      <Box aria-live="polite" sx={visuallyHidden}>
        {visibility.notMarried && showNotMarriedMessage
          ? t("spousalMaintenance.notMarried")
          : t("spousalMaintenance.questionsShown", { count: visibleQuestionCount })}
      </Box>
    </Stack>
  );
}
